import { useState, useEffect } from 'react';
import { api, InfluencerCatalogProduct, InfluencerCatalogProductDetail, CreateProductRequestData } from '../api';
import Layout from '../components/Layout';

function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-950/80 backdrop-blur-sm" onClick={onClose} />
      <div className={`card p-6 w-full ${sizeClasses[size]} relative z-10 animate-slide-up max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-display font-bold text-ink-50">{title}</h2>
          <button onClick={onClose} className="btn-ghost p-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const CATEGORIES = [
  'shirt',
  'hoodie',
  'sweatshirt',
  'pants',
  'shorts',
  'jacket',
  'hat',
  'bag',
  'accessory',
  'beauty',
  'other',
];

export default function InfluencerExplore() {
  const [products, setProducts] = useState<InfluencerCatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Product detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<InfluencerCatalogProductDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Request modal
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState<CreateProductRequestData>({
    vendor_catalog_product_id_fk: 0,
    wants_designer: 0,
  });
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestSuccess, setRequestSuccess] = useState(false);

  useEffect(() => {
    loadProducts();
  }, [filterCategory]);

  const loadProducts = async () => {
    try {
      const { products } = await api.browseCatalogProducts({
        category: filterCategory || undefined,
      });
      setProducts(products);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const openDetailModal = async (product: InfluencerCatalogProduct) => {
    setDetailLoading(true);
    setShowDetailModal(true);

    try {
      const { product: detail } = await api.getCatalogProductDetail(product.id);
      setSelectedProduct(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load product');
    } finally {
      setDetailLoading(false);
    }
  };

  const openRequestModal = (product: InfluencerCatalogProductDetail) => {
    setRequestForm({
      vendor_catalog_product_id_fk: product.id,
      wants_designer: 0,
      requested_retail: product.suggested_retail,
    });
    setRequestError('');
    setRequestSuccess(false);
    setShowRequestModal(true);
  };

  const handleSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setRequestLoading(true);
    setRequestError('');

    try {
      await api.createProductRequest(requestForm);
      setRequestSuccess(true);
      setTimeout(() => {
        setShowRequestModal(false);
        setShowDetailModal(false);
      }, 2000);
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setRequestLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const filteredProducts = products.filter((p) =>
    p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.vendor_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-display font-bold text-ink-50">Explore Products</h1>
          <p className="text-ink-400 mt-1">Browse and request products from our catalog</p>
        </div>

        {error && (
          <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-4 text-accent-400">
            {error}
          </div>
        )}

        {/* Search & Filters */}
        <div className="card p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  className="input w-full pl-10"
                  placeholder="Search products or vendors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterCategory('')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterCategory === ''
                    ? 'bg-accent-500 text-white'
                    : 'bg-ink-800 text-ink-300 hover:bg-ink-700'
                }`}
              >
                All
              </button>
              {CATEGORIES.slice(0, 6).map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilterCategory(cat)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                    filterCategory === cat
                      ? 'bg-accent-500 text-white'
                      : 'bg-ink-800 text-ink-300 hover:bg-ink-700'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.length === 0 ? (
            <div className="col-span-full card p-12 text-center text-ink-400">
              No products found matching your criteria.
            </div>
          ) : (
            filteredProducts.map((product) => (
              <div
                key={product.id}
                onClick={() => openDetailModal(product)}
                className="card overflow-hidden cursor-pointer hover:ring-1 hover:ring-accent-500/30 transition-all group"
              >
                {/* Placeholder image */}
                <div className="aspect-square bg-ink-800 flex items-center justify-center relative overflow-hidden">
                  <svg
                    className="w-16 h-16 text-ink-600 group-hover:scale-110 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div className="p-4">
                  <div className="mb-2">
                    <h3 className="font-medium text-ink-100 line-clamp-1 group-hover:text-accent-400 transition-colors">
                      {product.title}
                    </h3>
                    <p className="text-sm text-ink-400">{product.vendor_name}</p>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    <span className="text-xs bg-ink-800 text-ink-300 px-2 py-0.5 rounded capitalize">
                      {product.category}
                    </span>
                    {product.customizable ? (
                      <span className="text-xs bg-accent-500/20 text-accent-400 px-2 py-0.5 rounded">
                        Custom
                      </span>
                    ) : null}
                    {product.printing_available ? (
                      <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                        Print
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-baseline justify-between">
                    <div>
                      <span className="text-xs text-ink-500">From</span>
                      <span className="text-lg font-bold text-accent-400 ml-1">
                        {formatCurrency(product.min_retail)}
                      </span>
                    </div>
                    <span className="text-xs text-ink-500">
                      Suggested: {formatCurrency(product.suggested_retail)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Product Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title={selectedProduct?.title || 'Product Details'}
        size="lg"
      >
        {detailLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
          </div>
        ) : selectedProduct ? (
          <div className="space-y-6">
            {/* Images */}
            <div className="aspect-video bg-ink-800 rounded-lg flex items-center justify-center">
              {selectedProduct.images.length > 0 ? (
                <img
                  src={selectedProduct.images[0].image_url}
                  alt={selectedProduct.images[0].alt_text || selectedProduct.title}
                  className="w-full h-full object-contain rounded-lg"
                />
              ) : (
                <svg className="w-16 h-16 text-ink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              )}
            </div>

            {/* Info */}
            <div>
              <p className="text-ink-400 text-sm">{selectedProduct.vendor_name}</p>
              <h2 className="text-xl font-bold text-ink-100 mt-1">{selectedProduct.title}</h2>
              {selectedProduct.description && (
                <p className="text-ink-300 mt-3">{selectedProduct.description}</p>
              )}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2">
              <span className="text-sm bg-ink-800 text-ink-300 px-3 py-1 rounded-full capitalize">
                {selectedProduct.category}
              </span>
              {selectedProduct.customizable ? (
                <span className="text-sm bg-accent-500/20 text-accent-400 px-3 py-1 rounded-full">
                  Customizable
                </span>
              ) : null}
              {selectedProduct.printing_available ? (
                <span className="text-sm bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full">
                  Printing Available
                </span>
              ) : null}
              {selectedProduct.embroidery_available ? (
                <span className="text-sm bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full">
                  Embroidery Available
                </span>
              ) : null}
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-ink-800 rounded-lg">
              <div className="text-center">
                <div className="text-xs text-ink-500 uppercase tracking-wider">Base Cost</div>
                <div className="text-lg font-bold text-ink-200 mt-1">
                  {formatCurrency(selectedProduct.base_cost)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-ink-500 uppercase tracking-wider">Min Retail</div>
                <div className="text-lg font-bold text-ink-200 mt-1">
                  {formatCurrency(selectedProduct.min_retail)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-xs text-ink-500 uppercase tracking-wider">Suggested</div>
                <div className="text-lg font-bold text-accent-400 mt-1">
                  {formatCurrency(selectedProduct.suggested_retail)}
                </div>
              </div>
            </div>

            {/* MOQ */}
            <div className="flex items-center gap-2 text-sm text-ink-400">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Minimum order quantity: {selectedProduct.moq} unit(s)
            </div>

            {/* Action */}
            <button
              onClick={() => openRequestModal(selectedProduct)}
              className="btn-primary w-full py-3 text-lg"
            >
              Request This Product
            </button>
          </div>
        ) : null}
      </Modal>

      {/* Request Modal */}
      <Modal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        title="Request Product"
      >
        {requestSuccess ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-ink-100">Request Submitted!</h3>
            <p className="text-ink-400 mt-2">We'll review your request and get back to you soon.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmitRequest} className="space-y-4">
            {requestError && (
              <div className="bg-accent-500/10 border border-accent-500/20 rounded-lg p-3 text-accent-400 text-sm">
                {requestError}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">
                Requested Retail Price
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  min={selectedProduct?.min_retail}
                  className="input w-full pl-8"
                  value={requestForm.requested_retail || ''}
                  onChange={(e) =>
                    setRequestForm({ ...requestForm, requested_retail: parseFloat(e.target.value) })
                  }
                />
              </div>
              <p className="text-xs text-ink-500 mt-1">
                Minimum: {selectedProduct && formatCurrency(selectedProduct.min_retail)}
              </p>
            </div>

            {(selectedProduct?.customizable ||
              selectedProduct?.printing_available ||
              selectedProduct?.embroidery_available) && (
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">
                  Customization Details
                </label>
                <textarea
                  className="input w-full"
                  rows={3}
                  value={requestForm.requested_customization || ''}
                  onChange={(e) =>
                    setRequestForm({ ...requestForm, requested_customization: e.target.value })
                  }
                  placeholder="Describe your customization preferences (colors, placement, etc.)"
                />
              </div>
            )}

            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!requestForm.wants_designer}
                  onChange={(e) =>
                    setRequestForm({ ...requestForm, wants_designer: e.target.checked ? 1 : 0 })
                  }
                  className="w-5 h-5 rounded border-ink-600 text-accent-500 focus:ring-accent-500"
                />
                <span className="text-ink-200">I need help with design</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">
                Design Assets URL (optional)
              </label>
              <input
                type="url"
                className="input w-full"
                value={requestForm.design_assets_url || ''}
                onChange={(e) => setRequestForm({ ...requestForm, design_assets_url: e.target.value })}
                placeholder="https://drive.google.com/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-300 mb-1">Notes (optional)</label>
              <textarea
                className="input w-full"
                rows={2}
                value={requestForm.notes || ''}
                onChange={(e) => setRequestForm({ ...requestForm, notes: e.target.value })}
                placeholder="Any additional notes..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={() => setShowRequestModal(false)} className="btn-ghost">
                Cancel
              </button>
              <button type="submit" disabled={requestLoading} className="btn-primary">
                {requestLoading ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </Layout>
  );
}
